/* eslint-disable react-hooks/exhaustive-deps */
import Select from 'react-select'
import React, { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import Appointment from '../../components/Appointment/Appointment'
import SearchInput from '../../components/SearchInput/SearchInput'
import { getAppointments } from '../../services/api/appointments'
import { APPOINTMENT_STATES } from '../../helpers/constants'
import DefaultLoader from '../../components/Loaders/DefaultLoader'
import { OrderAppointment, OrderAppointmentState } from '../../model'
import Pagination from '../../components/Pagination/Pagination'
import { useUpdateEffect } from 'react-use'
import { getSearchParamsAsOb } from '../../helpers/utils'
import { getOrderDetailAppointments } from '../../services/api/orders'

interface SearchOptions {
    state?: OrderAppointmentState | undefined
    searchString?: string | undefined
    page: number
    pageSize: number
}

interface EnabledSearchParams {
    state?: string | undefined
    s?: string | undefined
    page?: string | undefined
}

const defaultSearchOptions: SearchOptions = {
    state: undefined,
    searchString: '',
    page: 1,
    pageSize: 10
}

const DynamicAppointments: React.FunctionComponent<{ canSearch?: boolean }> = ({ canSearch = false }) => {
    const { orderId } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loading, setLoading] = useState<boolean>(false)

    const [searchOptions, setSearchOptions] = useState<SearchOptions>({
        ...defaultSearchOptions
    })

    const onOrderDetail = orderId !== undefined

    const [appointments, setAppointments] = useState<OrderAppointment[]>([])
    const [total, setTotal] = useState<number>(0)

    const fetchAppointments = async () => {
        setLoading(true)

        const params = {
            ...searchOptions
        }

        try {
            let res
            if (onOrderDetail) {
                res = await getOrderDetailAppointments(parseInt(orderId), params)
            } else {
                res = await getAppointments(params)
            }

            setAppointments(res?.items || [])
            setTotal(res?.total || 0)
        } catch (e) {
            console.error(e)
            setAppointments([])
            setTotal(0)
        } finally {
            setLoading(false)
        }
    }

    const updateSearchParams = (params: EnabledSearchParams) => {
        const paramsToSet = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== undefined))
        setSearchParams(paramsToSet)
    }

    const handleSearchChange = (query: string) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        let params: EnabledSearchParams = { ...searchParamsObj, s: query || undefined }

        // Reset page to first when needed
        if (searchParams.has('page')) {
            params.page = '1'
        }
        updateSearchParams(params)
    }

    const handleStatusChange = (item: any) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        let params: EnabledSearchParams = { ...searchParamsObj, state: item ? item.value : undefined }

        // Reset page to first when needed
        if (searchParams.has('page')) {
            params.page = '1'
        }
        // @ts-ignore
        updateSearchParams(params)
    }

    const handlePageChange = (page: number) => {
        const searchParamsObj = getSearchParamsAsOb(searchParams)
        const params: EnabledSearchParams = { ...searchParamsObj, page }
        updateSearchParams(params)
    }

    const updateSearchOptions = () => {
        setSearchOptions({
            ...searchOptions,
            state: (searchParams.get('state') as OrderAppointmentState) || undefined,
            searchString: searchParams.get('s') || undefined,
            page: searchParams.get('page') ? parseInt(searchParams.get('page') || '1') : 1
        })
    }

    useEffect(() => {
        // Initialize based on search params or just load the first results
        if (searchParams.has('page') || searchParams.has('s') || searchParams.has('state')) {
            updateSearchOptions()
        } else {
            fetchAppointments()
        }
    }, [])

    useUpdateEffect(() => {
        fetchAppointments()
    }, [searchOptions])

    /** Listen to search params change */
    useUpdateEffect(() => {
        updateSearchOptions()
    }, [searchParams])

    return (
        <>
            {/* Searchbar */}
            {canSearch && (
                <div className='flex flex-wrap px-6 pt-6 gap-4 w-full'>
                    <SearchInput
                        initialValue={searchOptions.searchString}
                        placeholder='Zoek op opdracht of locatie'
                        onSearchChange={handleSearchChange}
                        className='w-full max-w-lg'
                    />
                    <Select
                        value={APPOINTMENT_STATES.find((s) => s.value === searchOptions.state) || null}
                        onChange={handleStatusChange}
                        options={APPOINTMENT_STATES}
                        placeholder='Alle statussen'
                        className='relative'
                        isClearable
                    />
                </div>
            )}
            {loading && (
                <div className='w-full h-full top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 items-center flex justify-center py-4 absolute bg-white/85 rounded-xl z-50'>
                    <DefaultLoader loadingMessage='Afspraken Laden...' />
                </div>
            )}
            {!loading && total === 0 ? (
                <div className='flex items-center justify-center w-full h-auto flex-grow'>
                    <div className='text-center text-gray-400 my-auto mx-auto w-full'>Geen afspraken</div>
                </div>
            ) : (
                <div className='pt-4 flex-grow'>
                    {appointments.map((app: OrderAppointment) => {
                        return <Appointment key={app.id} data={app} fetchAppointments={fetchAppointments} />
                    })}
                </div>
            )}

            {total > 10 && (
                <div className='px-2 justify-self-end'>
                    <Pagination
                        // @ts-ignore
                        page={searchOptions.page}
                        itemsPerPage={searchOptions.pageSize}
                        totalItems={total}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}
        </>
    )
}

export default DynamicAppointments
